import { Component, inject } from "@angular/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
    AcknowledgeAllNotificationsGraphql,
    AcknowledgeNotificationGraphql,
    MyNotificationsGraphql,
    UnreadNotificationCountGraphql,
} from "@shared/base/components/alerts/alerts/alerts-menu-query.graphql";
import { FixedOverlayDirective, DateModelPipe, UbbTranslatePipe } from "@ubisolutions/base";
import { Pagination } from "@ubisolutions/base/datasource";
import { GraphqlService } from "@ubisolutions/core";
import { distinctUntilChanged, interval, map, of, skip } from "rxjs";
import { MyNotificationsQuery } from "@graphql-generated/graphql";
import { AsyncPipe } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatCard, MatCardContent, MatCardFooter, MatCardHeader } from "@angular/material/card";
import { RouterLink } from "@angular/router";
import { MatBadge } from "@angular/material/badge";
import { ConnectedPosition } from "@angular/cdk/overlay";

type InboxNotification = MyNotificationsQuery["myNotifications"]["items"][number];
type InboxItem = InboxNotification["items"][number];

const MAX_ITEMS_SHOWN = 3;

@Component({
    selector: "app-alerts-menu",
    imports: [
        MatIconButton,
        MatIcon,
        AsyncPipe,
        MatCard,
        MatCardContent,
        MatCardFooter,
        RouterLink,
        MatBadge,
        FixedOverlayDirective,
        MatCardHeader,
        MatButton,
        UbbTranslatePipe,
        DateModelPipe,
    ],
    templateUrl: "./alerts-menu.component.html",
    styleUrl: "./alerts-menu.component.scss",
})
export class AlertsMenuComponent {
    private readonly graphqlService = inject(GraphqlService);

    private static readonly POLLING_INTERVAL_MS = 30 * 1000;
    protected readonly maxItemsShown = MAX_ITEMS_SHOWN;

    private readonly unreadCountQuery = this.graphqlService.singleQuery(UnreadNotificationCountGraphql)
        .observeWith(of({}));

    private readonly notificationsQuery = this.graphqlService.singleQuery(MyNotificationsGraphql)
        .observeWith(of({ querySetting: { paging: new Pagination(0, 10, true) } }));

    protected readonly unreadCount$ = this.unreadCountQuery.pipe(
        map(count => Number(count)),
    );

    protected readonly notifications$ = this.notificationsQuery.pipe(
        map(result => result.items),
    );

    private readonly pollingSubscription = interval(AlertsMenuComponent.POLLING_INTERVAL_MS).pipe(
        takeUntilDestroyed(),
    ).subscribe(() => this.unreadCountQuery.refresh());

    private readonly reloadSubscription = this.unreadCount$.pipe(
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(),
    ).subscribe(() => this.notificationsQuery.refresh());

    protected acknowledge(notification: InboxNotification) {
        if (notification.acknowledgedAt != null) return;
        this.graphqlService.mutate(AcknowledgeNotificationGraphql, { id: notification.id })
            .subscribe(() => this.refresh());
    }

    protected acknowledgeAll() {
        this.graphqlService.mutate(AcknowledgeAllNotificationsGraphql, {})
            .subscribe(() => this.refresh());
    }

    protected countsByType(notification: InboxNotification): [string, number][] {
        const counts = new Map<string, number>();
        notification.items.forEach(item => counts.set(item.notificationType, (counts.get(item.notificationType) ?? 0) + 1));
        return [...counts.entries()];
    }

    protected shownItems(notification: InboxNotification): InboxItem[] {
        return notification.items.slice(0, MAX_ITEMS_SHOWN);
    }

    protected sourceName(item: InboxItem): string {
        const payload = item.payload as Record<string, string> | null;
        return payload?.["alertConfigurationName"] ?? "";
    }

    protected isSevere(notification: InboxNotification) {
        return notification.maxSeverity === "CRITICAL" || notification.maxSeverity === "ERROR";
    }

    private refresh() {
        this.unreadCountQuery.refresh();
        this.notificationsQuery.refresh();
    }

    overlayPosition: ConnectedPosition = {
        originX: "start",
        originY: "bottom",
        overlayX: "end",
        overlayY: "top",
        offsetX: 64,
    };
}
